import { Avatar } from '@/components/ui/lib/avatar';
import { DICTIONARY } from '@/config/dictionary';
import { cn } from '@/utils/cn';
import type { WorkspaceAvatarProps } from '@/components/ui/types/workspace-avatar';

// Passed as Chakra style props rather than Tailwind classes so they override the Avatar
// recipe's own size instead of racing it across CSS layers.
const SIZE_STYLES = {
  sm: { boxSize: '9', rounded: 'md', fontSize: 'sm' },
  md: { boxSize: '10', rounded: 'xl', fontSize: 'md' },
  lg: { boxSize: '12', rounded: 'xl', fontSize: 'md' },
} as const;

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return DICTIONARY.dashboard.workspaceSwitcher.avatarFallbackInitials;
  }
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

/**
 * Workspace identity mark: the wholesaler's uploaded logo when one is stored, name initials
 * otherwise. Avatar.Root tracks the image's load status, so a `logoUrl` that 404s falls back
 * to initials on its own — no broken-image placeholder is ever shown.
 */
const WorkspaceAvatar = ({ name, logoUrl, size = 'md', className }: WorkspaceAvatarProps) => {
  const sizeStyles = SIZE_STYLES[size];
  const hasLogo = logoUrl != null && logoUrl.trim().length > 0;

  return (
    <Avatar.Root
      boxSize={sizeStyles.boxSize}
      rounded={sizeStyles.rounded}
      flexShrink="0"
      overflow="hidden"
      className={cn('bg-accent-subtle text-accent select-none', className)}
    >
      <Avatar.Fallback fontSize={sizeStyles.fontSize} fontWeight="700" color="inherit">
        {getInitials(name)}
      </Avatar.Fallback>
      {hasLogo && (
        <Avatar.Image
          src={logoUrl}
          alt={DICTIONARY.dashboard.workspaceSwitcher.logoAlt(name)}
          objectFit="contain"
          p="1"
        />
      )}
    </Avatar.Root>
  );
};

export default WorkspaceAvatar;
